import styles from "./UploadDataModal.module.scss";

interface Props {
  uploadData: any;
  setUploadDataModal: (value: boolean) => void;
}

const UploadDataModal = (props: Props) => {
  const { uploadData, setUploadDataModal } = props;
  if (!uploadData) return null;

  const closeModal = () => setUploadDataModal(false);

  const formatLabel = (key: string) =>
    key.replace(/([A-Z])/g, " $1").replace(/^./, (char) => char.toUpperCase());

  const hasRenderableContent = (value: unknown): boolean => {
    if (value === null || value === undefined || value === "") return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  };

  const renderDynamicValue = (value: unknown): React.ReactNode => {
    if (!hasRenderableContent(value)) return null;

    if (typeof value === "string") {
      return <p className={styles.valueText}>{value}</p>;
    }

    if (typeof value === "number" || typeof value === "boolean") {
      return <span className={styles.scalarValue}>{String(value)}</span>;
    }

    if (Array.isArray(value)) {
      if (value.every((item) => typeof item === "string")) {
        return (
          <ul className={styles.tagList}>
            {value.map((item, index) => (
              <li key={index} className={styles.tag}>
                {item}
              </li>
            ))}
          </ul>
        );
      }

      if (value.every((item) => typeof item === "object" && item !== null)) {
        return (
          <div className={styles.objectList}>
            {value.map((item, index) => (
              <div key={index} className={styles.objectItem}>
                {Object.entries(item as Record<string, unknown>).map(
                  ([fieldKey, fieldValue]) => (
                    <div key={fieldKey} className={styles.objectRow}>
                      <span className={styles.objectLabel}>
                        {formatLabel(fieldKey)}
                      </span>
                      <span className={styles.objectValue}>
                        {String(fieldValue ?? "")}
                      </span>
                    </div>
                  ),
                )}
              </div>
            ))}
          </div>
        );
      }

      return (
        <ul className={styles.plainList}>
          {value.map((item, index) => (
            <li key={index}>{String(item)}</li>
          ))}
        </ul>
      );
    }

    if (typeof value === "object") {
      return (
        <div className={styles.nestedObject}>
          {Object.entries(value as Record<string, unknown>).map(
            ([nestedKey, nestedValue]) => {
              const rendered = renderDynamicValue(nestedValue);
              if (!rendered) return null;

              return (
                <div key={nestedKey} className={styles.nestedSection}>
                  <p className={styles.nestedLabel}>{formatLabel(nestedKey)}</p>
                  <div className={styles.nestedContent}>{rendered}</div>
                </div>
              );
            },
          )}
        </div>
      );
    }

    return <p className={styles.valueText}>{String(value)}</p>;
  };
  

  if (typeof uploadData === "string") {
    return (
      <div className={styles.uploadDataModal}>
        <div className={styles.modalHeader}>
          <p className={styles.modalTitle}>Upload Result</p>
        </div>
        <div className={styles.modalBody}>
          <p className={styles.message}>{uploadData}</p>
        </div>
        <div className={styles.modalFooter}>
          <button className={styles.closeButton} onClick={closeModal}>
            Close
          </button>
        </div>
      </div>
    );
  }

  if (Array.isArray(uploadData)) {
    return (
      <div className={styles.uploadDataModal}>
        <div className={styles.modalHeader}>
          <p className={styles.modalTitle}>Upload Result</p>
        </div>
        <div className={styles.modalBody}>{renderDynamicValue(uploadData)}</div>
        <div className={styles.modalFooter}>
          <button className={styles.closeButton} onClick={closeModal}>
            Close
          </button>
        </div>
      </div>
    );
  }

  const data = uploadData as Record<string, unknown>;
  const message =
    typeof data.message === "string" ? data.message : "Upload Result";
  const processedCount = data.processedCount;
  const successCount = data.successCount;
  const hasStats = processedCount !== undefined || successCount !== undefined;

  const summaryKeys = ["message", "processedCount", "successCount"];
  const priorityOrder = ["errors", "warnings"];

  const sectionKeys = Object.keys(data)
    .filter(
      (key) => !summaryKeys.includes(key) && hasRenderableContent(data[key]),
    )
    .sort((a, b) => {
      const aIndex = priorityOrder.indexOf(a);
      const bIndex = priorityOrder.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });

  return (
    <div className={styles.mainContainer}>
      <div className={styles.modalHeader}>
        <p className={styles.modalTitle}>{message}</p>
        {hasStats && (
          <div className={styles.statsRow}>
            {processedCount !== undefined && (
              <span className={styles.stat}>
                Processed: <strong>{String(processedCount)}</strong>
              </span>
            )}
            {successCount !== undefined && (
              <span
                className={`${styles.stat} ${
                  Number(successCount) > 0
                    ? styles.statSuccess
                    : styles.statError
                }`}
              >
                Success: <strong>{String(successCount)}</strong>
              </span>
            )}
          </div>
        )}
      </div>
      <div className={styles.modalBody}>
        {sectionKeys.map((key) => {
          const rendered = renderDynamicValue(data[key]);
          if (!rendered) return null;

          const sectionClassName = [
            styles.section,
            key === "errors" ? styles.errorSection : "",
            key === "warnings" ? styles.warningSection : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <div key={key} className={sectionClassName}>
              <p className={styles.sectionLabel}>{formatLabel(key)}</p>
              <div className={styles.sectionContent}>{rendered}</div>
            </div>
          );
        })}
      </div>
      <div className={styles.modalFooter}>
        <button className={styles.closeButton} onClick={closeModal}>
          Close
        </button>
      </div>
    </div>
  );
};

export default UploadDataModal;
